import { LEVEL_UP_SEEN_HOOK } from "./channels.ts";
import { HEAL_SOUND_SET } from "./hooks.ts";
import { ShapingKind, shapeOf, type Shape, type TrimShape } from "./shaping.ts";

/**
 * Preparation at build (ADR-085's amendment of 2026-10-08): the build itself removes the silence an effect's master
 * starts with, cuts the silence it ends with down to a short faded tail, and mixes the master of a sound the page
 * places to one channel, all ahead of the loudness measuring and of a hook's low cut; the measuring alone then pads a
 * sound shorter than one gating block (the note of 2026-10-10), so every variant of a set is levelled by loudness. The
 * master on disk is never altered. This file holds the rule and the arithmetic; the build runs ffmpeg.
 */

/**
 * The level under which a master counts as silent, in dB under full scale.
 */
export const SILENCE_THRESHOLD_DB = -50;

/**
 * The shortest stretch under the threshold that counts as a run of silence, in milliseconds.
 */
export const SILENCE_MIN_MS = 5;

/**
 * How much of a master's trailing silence is kept after its last sound, in milliseconds, all of it fading out. An
 * end is cut only where the trailing silence is longer than this.
 */
export const KEPT_TAIL_MS = 100;

/**
 * The shortest stretch a prepared master's loudness is measured over, in milliseconds: one gating block of decision
 * 5's integrated loudness. A shorter sound is padded with silence to this length for the measuring only, so it has a
 * finite loudness and is levelled by the same rule as a longer variant of its hook.
 */
export const MEASURE_FLOOR_MS = 400;

/**
 * The word that names the filters the build prepares a master with. It changes when the filters themselves do, so
 * that every prepared file is named anew.
 */
export const PREPARATION_VERSION = "v2";

/**
 * The folders whose hooks are prepared: effects. Music, stingers and ambience are in none of them.
 */
export const PREPARED_FOLDERS: readonly string[] = Object.freeze(["sfx"]);

/**
 * The folders whose hooks are also mixed to one channel: every folder the page plays a sound from at an offset, which
 * the mixer places and pans (the note of 2026-10-10). A thing's sounds; a creature's and an NPC's; and another
 * player's steps, hurt and death, swing (a weapon's `use`) and heal (the potion's `use`). A hook is one file whoever
 * plays it, so the page's own steps, hurt, death, swing and potion are mono too.
 */
export const MONO_FOLDERS: readonly string[] = Object.freeze(["sfx/world", "sfx/creatures", "sfx/npcs", "sfx/player/walk", "sfx/player/combat", LEVEL_UP_SEEN_HOOK, "sfx/weapons", `sfx/items/${HEAL_SOUND_SET}`]);

/**
 * How one hook's masters are prepared: the rule's numbers, whether the master is mixed to one channel first, and the
 * shortest stretch its loudness is measured over.
 */
export interface MasterPreparation {
  readonly version: string;
  readonly thresholdDb: number;
  readonly minSilenceMs: number;
  readonly tailMs: number;
  readonly mono: boolean;
  readonly measureFloorMs: number;
}

/**
 * A stretch of a master under the silence threshold, in milliseconds from the master's start.
 */
export interface SilenceRun {
  readonly startMs: number;
  readonly endMs: number;
}

/**
 * Whether a hook is a folder of a list or lies in one.
 *
 * @param hook The hook.
 * @param folders The folders.
 * @returns True when one of them names the hook or a folder it lies in.
 */
function inFolders(hook: string, folders: readonly string[]): boolean {
  return folders.some((folder) => hook === folder || hook.startsWith(`${folder}/`));
}

/**
 * Whether a hook is an ability's sound the page places in the world: a projectile's `land`, heard where the shot lands
 * (P2.8-T84), while an ability's `use`, `hit` and `crit` keep their channels.
 *
 * @param hook The hook.
 * @returns True for `sfx/abilities/<id>/land`.
 */
function placedAbilityHook(hook: string): boolean {
  return /^sfx\/abilities\/[^/]+\/land$/.test(hook);
}

/**
 * How a hook's masters are prepared. A hook whose shaping is already a trim is not prepared at all: its times count
 * from the master's start and were chosen for that recording, so it is neither trimmed again nor mixed.
 *
 * @param hook The hook, or a music track's folder and slug.
 * @param shape The hook's shaping; the table's by default.
 * @returns The preparation, or null for a hook outside the prepared folders or one with a trim of its own.
 */
export function preparationOf(hook: string, shape: Shape | null = shapeOf(hook)): MasterPreparation | null {
  if (!inFolders(hook, PREPARED_FOLDERS) || shape?.kind === ShapingKind.Trim) {
    return null;
  }
  return { version: PREPARATION_VERSION, thresholdDb: SILENCE_THRESHOLD_DB, minSilenceMs: SILENCE_MIN_MS, tailMs: KEPT_TAIL_MS, mono: inFolders(hook, MONO_FOLDERS) || placedAbilityHook(hook), measureFloorMs: MEASURE_FLOOR_MS };
}

/**
 * A preparation as text for a built file's hash: the version word, the threshold, the shortest run, the tail, the
 * mix and the measuring floor, so a change to any names a new file. The times a master is cut at are not part of it: they follow from the
 * master's bytes and these numbers.
 *
 * @param preparation The preparation.
 * @returns The key.
 */
export function preparationKey(preparation: MasterPreparation): string {
  return `${preparation.version} ${preparation.thresholdDb} ${preparation.minSilenceMs} ${preparation.tailMs} ${preparation.mono ? "mono" : "as-is"} ${preparation.measureFloorMs}`;
}

/**
 * The trim that prepares one master, from its length and its runs of silence. Only the run the master starts in and
 * the run it ends in are read; a quiet stretch inside the sound is part of it. The start is the leading run's end,
 * rounded down, and the sound's end is the trailing run's start, rounded up, so nothing over the threshold is cut or
 * faded. The end is cut only where the trailing run is longer than the tail, and then the tail is kept and fades out;
 * otherwise the master keeps its end and no fade is added.
 *
 * @param durationMs The master's length in milliseconds.
 * @param silences Its runs of silence in order, the last ending at the length when the master ends in silence.
 * @param preparation The preparation, for its tail.
 * @returns The trim, or null when neither end has silence to cut, when the master is silent throughout, or when the
 *   length is not a number above 0.
 */
export function preparedTrim(durationMs: number, silences: readonly SilenceRun[], preparation: MasterPreparation): TrimShape | null {
  if (!Number.isFinite(durationMs) || durationMs <= 0) {
    return null;
  }
  const first = silences[0];
  const last = silences[silences.length - 1];
  const lead = first !== undefined && first.startMs <= 0 ? first : null;
  if (lead !== null && lead.endMs >= durationMs) {
    return null;
  }
  const startMs = lead === null ? 0 : Math.max(0, Math.floor(lead.endMs));
  const soundEndMs = last !== undefined && last.endMs >= durationMs ? Math.max(startMs, Math.ceil(last.startMs)) : durationMs;
  const cutsEnd = durationMs - soundEndMs > preparation.tailMs;
  if (!cutsEnd && startMs === 0) {
    return null;
  }
  const lengthMs = cutsEnd ? soundEndMs + preparation.tailMs - startMs : Math.ceil(durationMs) - startMs;
  return { kind: ShapingKind.Trim, startMs, lengthMs, fadeMs: cutsEnd ? Math.min(preparation.tailMs, lengthMs) : 0 };
}
